import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Paperclip, X } from 'lucide-react';
import { api } from '@/lib/api';
import { classNames } from '@/lib/utils';

type Estado = 'subiendo' | 'subido' | 'error';

/**
 * Adjuntar a la vez la liquidación en PDF a la factura de Holded de varias
 * liquidaciones de gestión seleccionadas en el listado. Sirve para las que se
 * facturaron antes de que el paso 5 la adjuntara sola. Van de una en una, como
 * el envío por correo: un fallo no para el resto.
 */
export default function AdjuntoMasivo({
  liquidaciones,
  generarPdf,
  onLiquidacionActualizada,
  onCerrar,
}: {
  liquidaciones: any[];
  /** El mismo PDF que «Descargar», en base64 */
  generarPdf: (liq: any) => Promise<{ nombre: string; base64: string }>;
  onLiquidacionActualizada: (liq: any) => void;
  onCerrar: () => void;
}) {
  const filas = useMemo(() => liquidaciones.map(liq => {
    const factura = liq.holdedInvoice;
    const motivo = (liq.modelo || 'gestion') !== 'gestion' || liq.result?.tipo !== 'liquidacion'
      ? 'No es de gestión'
      : !factura?.id
        ? 'Sin factura en Holded'
        : factura.adjunto
          ? 'Ya la lleva adjunta'
          : null;
    return { liq, factura, motivo };
  }), [liquidaciones]);

  const aSubir = filas.filter(f => !f.motivo);
  const [estados, setEstados] = useState<Record<string, { estado: Estado; detalle?: string }>>({});
  const [subiendo, setSubiendo] = useState(false);
  const [terminado, setTerminado] = useState(false);
  const hechas = Object.values(estados).filter(e => e.estado === 'subido').length;
  const fallidas = Object.values(estados).filter(e => e.estado === 'error').length;

  async function subirTodas() {
    setSubiendo(true);
    for (const f of aSubir) {
      setEstados(prev => ({ ...prev, [f.liq.id]: { estado: 'subiendo' } }));
      try {
        const r = await api.adjuntarLiquidacionHolded(f.liq.id, await generarPdf(f.liq));
        onLiquidacionActualizada(r.liquidacion);
        setEstados(prev => ({ ...prev, [f.liq.id]: { estado: 'subido' } }));
      } catch (err: any) {
        setEstados(prev => ({ ...prev, [f.liq.id]: { estado: 'error', detalle: err?.message || 'error' } }));
      }
    }
    setSubiendo(false);
    setTerminado(true);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 p-6 pb-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-sereno-dark">Adjuntar la liquidación en Holded</h3>
            <p className="text-sm text-sereno-dark/60 mt-1">
              A cada factura de Holded se le adjunta su liquidación en PDF, la misma que «Descargar». Holded no deja
              quitar un adjunto por la API, así que las que ya la llevan se saltan.
            </p>
          </div>
          {!subiendo && (
            <button onClick={onCerrar} className="text-sereno-dark/40 hover:text-sereno-dark" title="Cerrar"><X size={20} /></button>
          )}
        </div>

        <div className="overflow-y-auto divide-y divide-gray-100">
          {filas.map(f => {
            const st = estados[f.liq.id];
            return (
              <div key={f.liq.id} className={classNames('flex items-start gap-3 px-6 py-3', f.motivo ? 'opacity-60' : '')}>
                <Paperclip size={14} className="mt-1 shrink-0 text-sereno-dark/40" />
                <div className="flex-1 min-w-0 text-sm">
                  <p className="font-semibold text-sereno-dark truncate">
                    {f.liq.ownerName} <span className="font-normal text-sereno-dark/50">· {f.liq.periodo}</span>
                  </p>
                  <p className="text-sereno-dark/60 truncate">
                    {f.factura?.docNumber ? `Factura ${f.factura.docNumber}` : f.factura?.id ? 'Factura en borrador' : '—'}
                    {f.factura?.cuenta === 'test' ? ' · cuenta de pruebas' : ''}
                    {f.motivo ? ` · ${f.motivo}: se omite` : ''}
                  </p>
                  {st?.estado === 'error' && <p className="text-xs text-red-600 mt-0.5">{st.detalle}</p>}
                </div>
                <div className="shrink-0 w-24 text-right text-xs pt-0.5">
                  {st?.estado === 'subiendo' && <span className="inline-flex items-center gap-1 text-sereno-dark/60"><Loader2 size={12} className="animate-spin" /> Subiendo</span>}
                  {st?.estado === 'subido' && <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 size={12} /> Adjuntada</span>}
                  {st?.estado === 'error' && <span className="inline-flex items-center gap-1 text-red-600"><AlertTriangle size={12} /> Error</span>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 p-6 pt-4 border-t border-gray-100">
          <p className="text-sm text-sereno-dark/60">
            {terminado
              ? `${hechas} adjuntada${hechas === 1 ? '' : 's'}${fallidas ? ` · ${fallidas} con error` : ''}`
              : `${aSubir.length} de ${filas.length} se adjuntarán`}
          </p>
          {terminado ? (
            <button onClick={onCerrar} className="btn-primary">Cerrar</button>
          ) : (
            <div className="flex items-center gap-3">
              <button onClick={onCerrar} disabled={subiendo} className="btn-ghost">Cancelar</button>
              <button onClick={subirTodas} disabled={subiendo || aSubir.length === 0} className="btn-primary flex items-center gap-2">
                {subiendo ? <Loader2 className="animate-spin" size={16} /> : <Paperclip size={16} />}
                {subiendo ? 'Adjuntando…' : `Adjuntar ${aSubir.length}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
